import type { WorkshopJobStatusDTO } from "../../domain/contracts";

export const WORKSHOP_STATUS_LABELS: Record<WorkshopJobStatusDTO, string> = {
  received: "ຮັບລົດ",
  in_progress: "ກຳລັງສ້ອມ",
  waiting_parts: "ລໍຖ້າອາໄຫຼ່",
  completed: "ສຳເລັດ",
  paid: "ຈ່າຍແລ້ວ",
  cancelled: "ຍົກເລີກ",
};

/** คอลัมน์กระดานงานวันนี้ */
export const WORKSHOP_BOARD_COLUMNS: WorkshopJobStatusDTO[] = [
  "received",
  "in_progress",
  "waiting_parts",
  "completed",
];

/** ลำดับ stepper บนใบงาน (ไม่รวม cancelled) */
export const WORKSHOP_STEPPER_STATUSES: WorkshopJobStatusDTO[] = [
  "received",
  "in_progress",
  "waiting_parts",
  "completed",
  "paid",
];

export const WORKSHOP_QUOTE_STATUS_LABELS = {
  draft: "ຮ່າງ",
  sent: "ສົ່ງແລ້ວ",
  approved: "ອະນຸມັດແລ້ວ",
  rejected: "ປະຕິເສດ",
} as const;

export type WorkshopQuoteStatusFilter = keyof typeof WORKSHOP_QUOTE_STATUS_LABELS | "all";

export const WORKSHOP_QUOTE_STATUS_FILTERS: WorkshopQuoteStatusFilter[] = [
  "all",
  "draft",
  "sent",
  "approved",
  "rejected",
];

/** ป้าย CTA หลักตามสถานะถัดไป */
export function workshopPrimaryCtaLabel(
  to: WorkshopJobStatusDTO,
  from: WorkshopJobStatusDTO,
): string {
  if (from === "waiting_parts" && to === "in_progress") {
    return "ສ້ອມຕໍ່ — ອາໄຫຼ່ມາແລ້ວ";
  }
  switch (to) {
    case "in_progress":
      return "ເລີ່ມສ້ອມ";
    case "waiting_parts":
      return "ລໍຖ້າອາໄຫຼ່";
    case "completed":
      return "ສຳເລັດງານ";
    case "cancelled":
      return "ຍົກເລີກໃບງານ";
    default:
      return WORKSHOP_STATUS_LABELS[to];
  }
}

/** เลือกสถานะหลัก (ไปข้างหน้า) จาก allowedNext — ยกเลิกเป็นรอง */
export function pickWorkshopPrimaryNext(
  allowed: WorkshopJobStatusDTO[],
): WorkshopJobStatusDTO | null {
  const forward = allowed.find((s) => s !== "cancelled");
  return forward ?? allowed[0] ?? null;
}

export const WORKSHOP_PART_SOURCE_LABELS = {
  stock: "ຈາກສາງ",
  marketplace: "Marketplace",
  customer_supplied: "ລູກຄ້ານຳມາ",
} as const;

export const WORKSHOP_INVENTORY_STATE_LABELS = {
  reserved: "ຈອງແລ້ວ",
  consumed: "ຕັດສະຕ໋ອກແລ້ວ",
  released: "ປົດຈອງ",
  none: "ບໍ່ຈອງ",
} as const;

export function todayYmdLocal(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function formatWorkshopDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("lo-LA", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function formatWorkshopMoney(amount: number, currency: string): string {
  const n = amount.toLocaleString("lo-LA");
  if (currency === "LAK" || currency === "₭") return `₭ ${n}`;
  if (currency === "THB") return `฿ ${n}`;
  return `${n} ${currency}`;
}

export function quoteStatusToneClass(status: string): string {
  switch (status) {
    case "draft":
      return "bg-slate-100 text-slate-800 border-slate-200 dark:border-slate-500/30 dark:bg-slate-500/10 dark:text-slate-200";
    case "sent":
      return "bg-sky-100 text-sky-900 border-sky-200 dark:border-sky-500/35 dark:bg-sky-500/10 dark:text-sky-100";
    case "approved":
      return "bg-emerald-100 text-emerald-900 border-emerald-200 dark:border-emerald-500/35 dark:bg-emerald-500/10 dark:text-emerald-100";
    case "rejected":
      return "bg-red-100 text-red-900 border-red-200 dark:border-red-500/35 dark:bg-red-500/10 dark:text-red-100";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
}

export function statusToneClass(status: WorkshopJobStatusDTO): string {
  switch (status) {
    case "received":
      return "bg-sky-100 text-sky-900 border-sky-200 dark:border-sky-500/35 dark:bg-sky-500/10 dark:text-sky-100";
    case "in_progress":
      return "bg-amber-100 text-amber-950 border-amber-200 dark:border-amber-500/35 dark:bg-amber-500/10 dark:text-amber-100";
    case "waiting_parts":
      return "bg-orange-100 text-orange-950 border-orange-200 dark:border-orange-500/35 dark:bg-orange-500/10 dark:text-orange-100";
    case "completed":
      return "bg-emerald-100 text-emerald-900 border-emerald-200 dark:border-emerald-500/35 dark:bg-emerald-500/10 dark:text-emerald-100";
    case "paid":
      return "bg-emerald-600 text-white border-emerald-700 dark:border-emerald-400/45 dark:bg-emerald-600 dark:text-white";
    case "cancelled":
      return "bg-muted text-muted-foreground border-border";
    default:
      return "bg-muted border-border";
  }
}

export function inventoryStateToneClass(
  state: keyof typeof WORKSHOP_INVENTORY_STATE_LABELS,
): string {
  switch (state) {
    case "reserved":
      return "bg-sky-50 text-sky-800 border-sky-200 dark:border-sky-500/35 dark:bg-sky-500/10 dark:text-sky-100";
    case "consumed":
      return "bg-emerald-50 text-emerald-800 border-emerald-200 dark:border-emerald-500/35 dark:bg-emerald-500/10 dark:text-emerald-100";
    case "released":
      return "bg-muted text-muted-foreground border-border";
    case "none":
      return "bg-orange-50 text-orange-900 border-orange-200 dark:border-orange-500/35 dark:bg-orange-500/10 dark:text-orange-100";
    default:
      return "bg-muted border-border";
  }
}
